import { createContext, useContext, useState, useRef, useCallback, useEffect, ReactNode } from 'react';
import { safeStorage } from '@/lib/safeStorage';

const BGM_CACHE_KEY = 'gemu_bgm_audio';

interface BGMContextType {
  isPlaying: boolean;
  isLoading: boolean;
  togglePlay: () => Promise<void>;
}

const BGMContext = createContext<BGMContextType>({
  isPlaying: false,
  isLoading: false,
  togglePlay: async () => {},
});

export function useBGM() {
  return useContext(BGMContext);
}

export function BGMProvider({ children }: { children: ReactNode }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // AbortController ref to cancel in-flight BGM fetch on unmount or re-request
  const fetchAbortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (fetchAbortControllerRef.current) {
        fetchAbortControllerRef.current.abort();
        fetchAbortControllerRef.current = null;
      }
    };
  }, []);

  const getOrGenerateAudio = useCallback(async (): Promise<string> => {
    const cached = safeStorage.getItem(BGM_CACHE_KEY);
    if (cached) return cached;

    // Cancel any previous in-flight request
    if (fetchAbortControllerRef.current) {
      fetchAbortControllerRef.current.abort();
    }
    fetchAbortControllerRef.current = new AbortController();

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-bgm`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          prompt:
            'Gentle Japanese-inspired ambient music with soft piano, koto strings, and light wind chimes. Cherry blossom spring theme, peaceful and calming lo-fi atmosphere.',
          duration: 30,
        }),
        signal: fetchAbortControllerRef.current.signal,
      }
    );

    if (!response.ok) throw new Error('Failed to generate BGM');
    const data = await response.json();
    if (!data.audioContent) throw new Error('No audio content');

    const audioUrl = `data:audio/mpeg;base64,${data.audioContent}`;
    try {
      safeStorage.setItem(BGM_CACHE_KEY, audioUrl);
    } catch {
      // Storage quota exceeded
    }
    return audioUrl;
  }, []);

  const togglePlay = useCallback(async () => {
    if (isLoading) return;

    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }

    try {
      setIsLoading(true);

      if (!audioRef.current) {
        const audioUrl = await getOrGenerateAudio();
        const audio = new Audio(audioUrl);
        audio.loop = true;
        audio.volume = 0.3;
        audioRef.current = audio;
      }

      await audioRef.current.play();
      setIsPlaying(true);
    } catch (error) {
      console.error('BGM playback error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isPlaying, isLoading, getOrGenerateAudio]);

  return (
    <BGMContext.Provider value={{ isPlaying, isLoading, togglePlay }}>
      {children}
    </BGMContext.Provider>
  );
}
